/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2019 ICEMEDX, Inc.
***********************************************/
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { XmlparseService } from '../xml/xmlparse.service';

import { AlertService } from "../../services/alert/alert.service";
import { report, ReportBarCodeType, reportDataField, ReportDataTypes, reportField, reportObject, ReportObjectType, reportSection, ReportSectionTypes, reportSummationField, reportSystemField, ReportSystemFieldTypes, reportTextField, Report_Alignment, Report_Border_Style, Report_Page_Sizes } from "../../models/reports/report";
import { reportindex } from "../../models/reports/reportindex";
import { IceTypes } from "../../models/icetypes";
import { Iceutility } from "../../services/iceutility";
import { LoginService } from '../../login/login.service';
import { rptobject } from '../../models/reports/subs/rptobject';
import { IcePoint, IceRect } from '../../components/icemultimedia/IceMultimediaStroke';
import { rptsection } from '../../models/reports/subs/rptsection';
import { reportfilter } from '../../models/reports/subs/reportfilter';
import { IceMultimediaPage } from '../../components/icemultimedia/IceMultimediaPage';
import { reportdata } from '../../models/reports/reportdata';

declare var require: any;

export interface IReportListItem {
  report_key: string;
  name: string;
  type: string;
  run_frequency: string;
  description: string;
  source: string;
  modified: any;
  modified_by: string;
}

export class ReportListObject implements IReportListItem {
  report_key: string;
  name: string;
  type: string;
  run_frequency: string;
  description: string;
  source: string;
  modified: any;
  modified_by: string;

  constructor(key: string, name: string,
      type: string,
      run_freq: string,
      desc: string,
      src: string,
      mod: any,
      modby: string) {
    this.report_key = key;
    this.name = name;
    this.type = type;
    this.run_frequency = run_freq;
    this.description = desc;
    this.source = src;
    this.modified = mod;
    this.modified_by = modby;
  }
}

@Injectable()
export class ReportService extends XmlparseService {

  constructor(private http: HttpClient, private alertSvc: AlertService, private loginSvc: LoginService) {
    super(alertSvc, loginSvc);
  }

  /////////////////////////////
  // API Operations
  public getReports(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, keywords: string = null, type: IceTypes.ReportType = IceTypes.ReportType.rtUndefined): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    if (keywords == null)
      keywords = "";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=reports&domaintype=" + acttype.toString() + "&key=&subkeys=" + "keywords:" + keywords + ";type:" + type.toString();
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseReports(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getReport(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, template: boolean, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=report&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=template:" + template.toString();
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseReport(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getData(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api +"/default.aspx?domain=" + act + "&data=reportdata&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseReportData(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public updateReport(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, rpt: report): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (rpt == null) throw "Report object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api +"/default.aspx?domain=" + act + "&data=report&domaintype=" + acttype.toString() + "&key=" + rpt.report_key + "&subkeys=";

    let tblname = rpt.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(rpt) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseReport(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public removeReportSigner(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, rptData: reportdata, reviewerkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (rptData == null) throw "Report data object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=removesigner&domaintype=" + acttype.toString() + "&key=" + rptData.report_key + "&subkeys=reviewerkey:" + reviewerkey;

    let tblname = rptData.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(rptData) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseReportData(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  static getReportTypes(): any {
    var types = [];
    try {
      types.push({ value: IceTypes.ReportType.rtClincialData.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtClincialData) });
      types.push({ value: IceTypes.ReportType.rtScheduling.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtScheduling) });
      types.push({ value: IceTypes.ReportType.rtMedicalCodes.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtMedicalCodes) });
      types.push({ value: IceTypes.ReportType.rtAccountingSummary.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtAccountingSummary) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getReportType(type: any) {
    switch (<IceTypes.ReportType>Iceutility.toLong(type)) {
      case IceTypes.ReportType.rtClincialData:
        return "Clinical Data";
      case IceTypes.ReportType.rtScheduling:
        return "Scheduling / Admissions";
      case IceTypes.ReportType.rtMedicalCodes:
        return "Medical Codes";
      case IceTypes.ReportType.rtAccountingSummary:
        return "Accounting";
    }
    return "";
  }

  private parseReports(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["REPORT_INDEX"]);
  }

  private parseReport(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["REPORT"]);
  }

  private parseReportData(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["REPORT_DATA"]);
  }
}
